import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuthorization } from 'framework/authorization';
import { POSITION_SLOT_EMPLOYEE_VALIDATION } from './constants';
import { PositionSlotListItem } from './types';
import usePositionSlotValidationService from './usePositionSlotValidationService';

type PositionSlotValidationContextValue = {
  errorsByEmployeeAlias: Record<string, string[]>;
};

export const PositionSlotValidationContext = createContext<PositionSlotValidationContextValue>({
  errorsByEmployeeAlias: {},
});

export const usePositionSlotValidationErrors = (employeeAlias: string | null | undefined): string[] => {
  const { errorsByEmployeeAlias } = useContext(PositionSlotValidationContext);

  if (!employeeAlias) {
    return [];
  }

  return errorsByEmployeeAlias[employeeAlias] ?? [];
};

const buildEmployeeToPositionSlotMap = (positionSlots: PositionSlotListItem[]): Record<string, string> =>
  positionSlots.reduce<Record<string, string>>((employeeToPositionSlot, positionSlot) => {
    if (positionSlot.employeeAlias) {
      employeeToPositionSlot[positionSlot.employeeAlias] = positionSlot.id;
    }

    return employeeToPositionSlot;
  }, {});

const buildValidationKey = (positionSlots: PositionSlotListItem[]): string =>
  positionSlots
    .filter(positionSlot => positionSlot.employeeAlias)
    .map(positionSlot => `${positionSlot.id}:${positionSlot.employeeAlias}`)
    .join('|');

const mapValidationErrors = (validationResponse: Record<string, { errors?: string[] }>): Record<string, string[]> =>
  Object.entries(validationResponse).reduce<Record<string, string[]>>(
    (errorsByEmployeeAlias, [employeeAlias, result]) => {
      if (result.errors?.length) {
        errorsByEmployeeAlias[employeeAlias] = result.errors;
      }

      return errorsByEmployeeAlias;
    },
    {},
  );

export const useComputePositionSlotValidationErrors = (
  positionSlots: PositionSlotListItem[],
  loading: boolean,
  extraCacheKey?: string,
): Record<string, string[]> => {
  const { isGranted } = useAuthorization();
  const { validateEmployeePositionSlots } = usePositionSlotValidationService();
  const [errorsByEmployeeAlias, setErrorsByEmployeeAlias] = useState<Record<string, string[]>>({});
  const lastFetchedRequestKeyRef = useRef<string | null>(null);
  const validateEmployeePositionSlotsRef = useRef(validateEmployeePositionSlots);

  const canValidate = isGranted(POSITION_SLOT_EMPLOYEE_VALIDATION);

  validateEmployeePositionSlotsRef.current = validateEmployeePositionSlots;

  const employeeToPositionSlot = useMemo(() => buildEmployeeToPositionSlotMap(positionSlots), [positionSlots]);

  const validationRequestKey = useMemo(
    () => `${extraCacheKey ?? ''}|${buildValidationKey(positionSlots)}`,
    [extraCacheKey, positionSlots],
  );

  useEffect(() => {
    if (loading || !canValidate) {
      return;
    }

    if (validationRequestKey === lastFetchedRequestKeyRef.current) {
      return;
    }

    lastFetchedRequestKeyRef.current = validationRequestKey;

    if (Object.keys(employeeToPositionSlot).length === 0) {
      setErrorsByEmployeeAlias(previousErrors => (Object.keys(previousErrors).length === 0 ? previousErrors : {}));
      return;
    }

    let isCancelled = false;

    validateEmployeePositionSlotsRef
      .current(employeeToPositionSlot)
      .then(validationResponse => {
        if (!isCancelled) {
          setErrorsByEmployeeAlias(mapValidationErrors(validationResponse));
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setErrorsByEmployeeAlias(previousErrors => (Object.keys(previousErrors).length === 0 ? previousErrors : {}));
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [loading, canValidate, validationRequestKey, employeeToPositionSlot]);

  return errorsByEmployeeAlias;
};
